import {Injectable} from '@angular/core';
import {CampaignsSortType} from '../../types/campaigns.type';
import {OrderType} from '../../types/order.type';
import {Observable, of, throwError} from 'rxjs';
import {IResponse} from '@interfaces/response';
import {ICampaign, ICampaignActive, ICampaignHistory, ISingleCampaign} from '@interfaces/campaigns';
import {createHttpParams} from '@shared/functions';
import {HttpClient, HttpResponseBase} from '@angular/common/http';
import {ErrorService} from '@services/error/error.service';
import {catchError, share, switchMap, takeWhile} from 'rxjs/operators';
import {CampaignTypeEnum} from '@shared/enums/campaign-type.enum';
import {ModalService} from '@services/modal/modal.service';
import {TranslateService} from '@ngx-translate/core';
import {Store} from '@ngxs/store';
import {ToggleGlobalLoader} from '@store/loader';
import {IDeviceActivity} from '../../models/device-activity.model';

@Injectable({
  providedIn: 'root'
})
export class CampaignsService {

  constructor(private http: HttpClient,
              private dialog: ModalService,
              private errorService: ErrorService,
              private translate: TranslateService,
              private store: Store) {
  }

  public getCampaigns(type: CampaignTypeEnum, {limit, skip = 0, search, sort, order}:
    {
      limit: number, skip: number, search: string,
      sort: CampaignsSortType;
      order: OrderType;
    }
  ): Observable<IResponse<ICampaign | ICampaignActive | ICampaignHistory>> {
    const params = createHttpParams({
      limit, skip, sort, order, search
    });

    return this.http.get<IResponse<ICampaign | ICampaignActive | ICampaignHistory>>(`tv-owner/campaigns/${type}`, {params})
      .pipe(
        share(),
        catchError((err) => {
          this.errorService.showError(err);
          return throwError(err);
        })
      );
  }

  public getCampaignById(id: number): Observable<ISingleCampaign> {
    return this.http.get<ISingleCampaign>(`tv-owner/campaigns/${id}`).pipe(
        share(),
        catchError((err) => {
          this.errorService.showError(err);
          return throwError(err);
        })
      );
    }

  approveCampaign(id: number): Observable<HttpResponseBase> {
    this.store.dispatch(new ToggleGlobalLoader(true));
    const stream$ = this.http.post<HttpResponseBase>(`tv-owner/campaigns/approve`, {id}, {observe: 'response'})
      .pipe(
        share(),
        catchError((err) => {
          this.errorService.showError(err);
          return throwError(err);
        }),
      );

    stream$.pipe(
      takeWhile(res => res.status === 200),
      switchMap(() => this.translate.get('tv-owner.campaigns.campaignApproved'))
    ).subscribe((msg) => {
      this.store.dispatch(new ToggleGlobalLoader(false));
      this.dialog.showMsg(msg, 500, {cancel: false});
    });

    return stream$;
  }

  rejectCampaign(data: { id: number, reason: string }): Observable<HttpResponseBase> {
    this.store.dispatch(new ToggleGlobalLoader(true));
    const stream$ = this.http.post<HttpResponseBase>('tv-owner/campaigns/reject', {...data}, {observe: 'response'})
      .pipe(
        share(),
        catchError((err) => {
          this.errorService.showError(err);
          return throwError(err);
        })
      );

    stream$.pipe(
      takeWhile((res) => res.status === 200),
      switchMap(() => this.translate.get('tv-owner.campaigns.campaignRejected'))
    ).subscribe((msg) => {
      this.store.dispatch(new ToggleGlobalLoader(false));
      this.dialog.showMsg(msg, 500, {cancel: false});
    });

    return stream$;
  }

  getDeviceActivity(campaignId: number): Observable<IDeviceActivity> {
    return this.http.get<IDeviceActivity>(`tv-owner/campaigns/activity/${campaignId}`)
      .pipe(
        catchError((err) => {
          this.errorService.showError(err);
          return throwError(err);
        })
      );
  }
}
